import { Component, DestroyRef, signal, Output, EventEmitter, input, effect, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonsComponent } from '@/app/shared/buttons/buttons.component';
import { DesyAngularModule } from 'desy-angular';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectButtonModule } from 'primeng/selectbutton';
import { MultiSelectModule } from 'primeng/multiselect';
import { ConfirmationService } from 'primeng/api';
import { Router } from '@angular/router';
import { getError } from '@/app/utils/form-errors';
import { AiSystemDeployment, Step2Deployment } from '@/app/interfaces/aiSystemDeployment';
import {
  DeploySystemCorrectionObservedField,
  DeploySystemCorrectionObservedFieldStatus,
} from '@/app/interfaces/deploy-system-correction';
import { ObservedFieldAlertComponent } from '@/app/shared/observed-field-alert/observed-field-alert/observed-field-alert.component';
import { setupObservedFieldsTracking } from '../helpers/observed-fields.helper';
import { setupConditionalRequiredValidation, setupRequiredValidationBySignal } from '../helpers/validation.helper';
import { binaryOptions } from '../helpers/binary-options.interface';

@Component({
  selector: 'app-deploy-system-correction-step-2',
  standalone: true,
  templateUrl: './deploy-system-correction-step-2.component.html',
  styleUrls: ['./deploy-system-correction-step-2.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    AutoCompleteModule,
    ButtonsComponent,
    DesyAngularModule,
    DatePickerModule,
    SelectButtonModule,
    MultiSelectModule,
    ObservedFieldAlertComponent,
  ],
  providers: [ConfirmationService],
})
export class DeploySystemCorrectionStep2Component {
  @Output() stepCompleted = new EventEmitter<Step2Deployment>();
  @Output() goToPreviousStep = new EventEmitter<Step2Deployment>();
  @Output() summaryRequested = new EventEmitter<Step2Deployment>();
  @Output() observedFieldStatusChanged = new EventEmitter<DeploySystemCorrectionObservedFieldStatus>();

  existingData = input<Partial<AiSystemDeployment>>();
  observedFields = input<DeploySystemCorrectionObservedField[]>([]);
  pendingObservedFields = input<DeploySystemCorrectionObservedField[]>([]);

  form: FormGroup;
  getError = getError;
  id_system: string = '';

  binaryOptions = binaryOptions;

  formSubmitted = signal(false);
  highRiskFieldsRequired = signal(false);
  userInfoRequired = signal(false);
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private fb: FormBuilder,
    private router: Router
  ) {
    const url = window.location.pathname;
    const parts = url.split('/');
    this.id_system = parts[parts.length - 2];

    this.form = this.fb.group({
      usageInstructions: [null, Validators.maxLength(200)],
      humanSupervision: null,
      inputDataQuality: [null, Validators.maxLength(200)],
      qualityMonitoring: [null, Validators.maxLength(500)],
      logRetention: [null, Validators.maxLength(200)],
      fria: [null, Validators.maxLength(200)],
      dpia: [null, Validators.maxLength(200)],
      naturalPersonInteraction: [null, Validators.required],
      contentGeneration: [null, Validators.required],
      biometricsEmotions: [null, Validators.required],
      publicInterestText: [null, Validators.required],
      userInformation: [null, Validators.maxLength(500)],
    });

    setupConditionalRequiredValidation(
      this.form,
      this.userInfoRequired,
      ['userInformation'],
      ['naturalPersonInteraction', 'contentGeneration', 'biometricsEmotions', 'publicInterestText']
    );

    setupRequiredValidationBySignal(this.form, this.highRiskFieldsRequired, [
      'usageInstructions',
      'humanSupervision',
      'inputDataQuality',
      'qualityMonitoring',
      'logRetention',
      'fria',
      'dpia',
    ]);

    effect(() => {
      const data = this.existingData();
      if (data) {
        this.form.patchValue(data);
      }
      const isHighRisk = data?.systemClassification === 'alto';
      this.highRiskFieldsRequired.set(isHighRisk);
    });

    setupObservedFieldsTracking(this.form, this.observedFields, this.destroyRef, (status) =>
      this.observedFieldStatusChanged.emit(status)
    );
  }

  async onSave() {
    const formData = this.form.value;
    this.stepCompleted.emit(formData);
  }

  backPage(): void {
    const formData = this.form.value;
    this.goToPreviousStep.emit(formData);
  }

  goToSummary(): void {
    const formData = this.form.value;
    this.summaryRequested.emit(formData);
  }

  cancel(): void {
    this.router.navigate([`/catalogo-ia/sistemas-de-ia/listado-sistemas`]);
  }

  confirm() {
    this.formSubmitted.set(true);
    if (this.form.invalid || this.hasPendingObservedFieldsInStep()) {
      this.form.markAllAsTouched();
      return;
    }

    this.onSave();
  }

  isObserved(field: DeploySystemCorrectionObservedField): boolean {
    return this.pendingObservedFields().includes(field);
  }

  isAnyObserved(fields: DeploySystemCorrectionObservedField[]): boolean {
    return fields.some((field) => this.isObserved(field));
  }

  hasPendingObservedFieldsInStep(): boolean {
    return this.pendingObservedFields().some((field) => !!this.form.get(field));
  }
}
